import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios'
import { isPendingVerificationResponse } from './pendingVerificationSession'
import authSession from './authSession'

// Billing API client using access + refresh JWT tokens from localStorage.
// Configure base URL via Vite env: VITE_BILLING_API_BASE_URL
type AxiosData = AxiosResponse['data']
type HoardTokenResponse = {
  access_token?: string
  refresh_token?: string
  token?: string
  force_password_change?: boolean
  requires_payment_method?: boolean
  package_card_optional?: boolean
  requires_email_verification?: boolean
  pending_session?: unknown
  scope?: string
}

class BillingApiClient {
  private client: AxiosInstance

  constructor() {
  // Use relative baseURL by default to inherit current origin/protocol
  // If explicitly provided, honor VITE_BILLING_API_BASE_URL
  const env = (import.meta as ImportMeta & { env?: Record<string, string | undefined> }).env
  const baseURL = env?.VITE_BILLING_API_BASE_URL || ''
    this.client = axios.create({ baseURL })

    // Attach bearer token and JSON defaults
    this.client.interceptors.request.use(async (config) => {
      config.headers = config.headers || {}
      const tokenResult = await authSession.getValidAccessToken()
      if (tokenResult.status === 'available') {
        config.headers.Authorization = `Bearer ${tokenResult.accessToken}`
      }
      config.headers.Accept = 'application/json'
      config.headers['X-Requested-With'] = 'XMLHttpRequest'
      return config
    })

    // Response interceptor: auto-refresh on 401
    this.client.interceptors.response.use(
      (response) => response,
      async (error) => {
        const original = error.config
        if (error.response?.status === 401 && !original._retry) {
          original._retry = true
          const failedAccessToken = authSession.getAccessToken()
          const refreshed = await authSession.refreshAccessToken({ failedAccessToken })
          if (refreshed.status === 'available') {
            original.headers = original.headers || {}
            original.headers.Authorization = `Bearer ${refreshed.accessToken}`
            return this.client(original)
          }
          if (refreshed.status === 'expired') authSession.expireSession()
        }
        return Promise.reject(error)
      }
    )
  }

  // Obtain a JWT from Hoard using the user's panel identifier (email or username) and password.
  // Uses nginx proxy path /hoard-auth/token -> $HOARD_HOST/auth/token.
  async loginWithCredentials(identifier: string, password: string): Promise<HoardTokenResponse & { token: string }> {
    const resp = await axios.post<HoardTokenResponse>(
      '/hoard-auth/token',
      { email: identifier, password },
      { withCredentials: false, validateStatus: (s) => s < 500 }
    )
    if (resp.status === 404 || resp.status === 405) {
      throw new Error('Hoard auth endpoint not available. Check frontend nginx proxy to /hoard-auth/.')
    }
    const accessToken = resp.data?.access_token || resp.data?.token || ''
    const refreshToken = resp.data?.refresh_token || ''
    const force_password_change = resp.data?.force_password_change || false
    const requires_payment_method = resp.data?.requires_payment_method || false
    const requires_email_verification = resp.data?.requires_email_verification || false
    const pending = isPendingVerificationResponse(resp.data)
    if (typeof window !== 'undefined') {
      if (accessToken && !pending) {
        authSession.storeCredentials(accessToken, refreshToken)
      }
      // Store force_password_change in sessionStorage for cross-component access
      if (force_password_change) {
        try { sessionStorage.setItem('force_password_change', 'true') } catch { /* storage may be blocked */ }
      }
    }
    return {
      ...resp.data,
      token: accessToken,
      force_password_change,
      requires_payment_method,
      requires_email_verification,
    }
  }

  get<T = AxiosData>(url: string, config?: AxiosRequestConfig) {
    return this.client.get<T>(url, config)
  }
  post<T = AxiosData>(url: string, data?: unknown, config?: AxiosRequestConfig) {
    return this.client.post<T>(url, data, config)
  }
  put<T = AxiosData>(url: string, data?: unknown, config?: AxiosRequestConfig) {
    return this.client.put<T>(url, data, config)
  }
  patch<T = AxiosData>(url: string, data?: unknown, config?: AxiosRequestConfig) {
    return this.client.patch<T>(url, data, config)
  }
  delete<T = AxiosData>(url: string, config?: AxiosRequestConfig) {
    return this.client.delete<T>(url, config)
  }
}

export const billingApi = new BillingApiClient()
export default billingApi
